// AssignmentEditor — POST or PATCH; supports the Edit-end-date-only mode
// for Admin on Completed agreements (frontend-design §4 F-2).

import { useEffect, useMemo } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import {
  assignmentsApi,
  peopleApi,
  type AssignmentCreateBody,
  type AssignmentPatchBody,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import type { Agreement, Assignment, Person, Principal } from '@/types/domain';

interface Props {
  open: boolean;
  onClose: () => void;
  agreement: Agreement;
  assignment: Assignment | null;
  endDateOnly: boolean;
  principal: Principal | null;
}

interface FormShape {
  person_id: string;
  role: string;
  bill_rate: string;
  cost_rate: string;
  allocation_pct: string;
  start_date: string;
  end_date: string;
}

const KNOWN: string[] = [
  'person_id',
  'role',
  'bill_rate',
  'cost_rate',
  'allocation_pct',
  'start_date',
  'end_date',
];

export function AssignmentEditor({
  open,
  onClose,
  agreement,
  assignment,
  endDateOnly,
  principal,
}: Props) {
  void principal;
  const isEdit = !!assignment;
  const qc = useQueryClient();
  const toast = useToast();

  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
    enabled: open,
  });
  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [peopleQ.data]);

  const defaults = useMemo<FormShape>(
    () =>
      assignment
        ? {
            person_id: assignment.person_id,
            role: assignment.role ?? '',
            bill_rate: assignment.bill_rate,
            cost_rate: assignment.cost_rate,
            allocation_pct: String(assignment.allocation_pct),
            start_date: assignment.start_date,
            end_date: assignment.end_date ?? '',
          }
        : {
            person_id: '',
            role: '',
            bill_rate: '',
            cost_rate: '',
            allocation_pct: '100',
            start_date: agreement.start_date,
            end_date: '',
          },
    [agreement, assignment],
  );

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<FormShape>({ defaultValues: defaults });

  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(open && isDirty);

  // Pre-fill cost_rate from selected Person on POST.
  const personId = watch('person_id');
  useEffect(() => {
    if (assignment) return; // edit mode: don't override
    if (!personId) return;
    const person = peopleById.get(personId);
    if (person?.cost_rate && !watch('cost_rate')) {
      setValue('cost_rate', person.cost_rate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, peopleById]);

  const createM = useMutation({
    mutationFn: (body: AssignmentCreateBody) =>
      assignmentsApi.create(agreement.id, body),
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignmentPatchBody }) =>
      assignmentsApi.update(id, body),
  });

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    try {
      if (isEdit && assignment) {
        const body: AssignmentPatchBody = {};
        if (endDateOnly) {
          body.end_date = values.end_date ? values.end_date : null;
        } else {
          if (dirtyFields.role)
            body.role = values.role ? values.role : null;
          if (dirtyFields.bill_rate) body.bill_rate = values.bill_rate;
          if (dirtyFields.cost_rate) body.cost_rate = values.cost_rate;
          if (dirtyFields.allocation_pct)
            body.allocation_pct = Number(values.allocation_pct);
          if (dirtyFields.start_date) body.start_date = values.start_date;
          if (dirtyFields.end_date)
            body.end_date = values.end_date ? values.end_date : null;
        }
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await updateM.mutateAsync({ id: assignment.id, body });
        toast.show({ severity: 'success', message: 'Assignment updated.' });
      } else {
        const body: AssignmentCreateBody = {
          person_id: values.person_id,
          role: values.role || null,
          bill_rate: values.bill_rate,
          cost_rate: values.cost_rate,
          allocation_pct: Number(values.allocation_pct),
          start_date: values.start_date,
          end_date: values.end_date ? values.end_date : null,
        };
        await createM.mutateAsync(body);
        toast.show({ severity: 'success', message: 'Assignment created.' });
      }
      void qc.invalidateQueries({
        queryKey: qk.agreementAssignments(agreement.id),
      });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (mapped.formMessage)
        toast.show({ severity: 'error', message: mapped.formMessage });
    }
  };

  const title = isEdit
    ? endDateOnly
      ? 'Edit assignment end date'
      : 'Edit assignment'
    : 'Add assignment';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      role="dialog"
      variant="drawer"
      canClose={() => {
        if (!isDirty) return true;
        return window.confirm('Discard unsaved changes?');
      }}
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        {!endDateOnly ? (
          <div className="form-field">
            <label htmlFor="ae-person">
              Person<span className="form-required" aria-hidden="true">*</span>
            </label>
            {isEdit ? (
              <input
                value={
                  peopleById.get(assignment!.person_id)?.name ??
                  assignment!.person_id
                }
                readOnly
                disabled
              />
            ) : (
              <select
                id="ae-person"
                disabled={isSubmitting || peopleQ.isPending}
                {...register('person_id', { required: 'Choose a person.' })}
              >
                <option value="">Select a person…</option>
                {peopleQ.data?.data.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.email})
                  </option>
                ))}
              </select>
            )}
            {errors.person_id ? (
              <span className="form-error">{errors.person_id.message}</span>
            ) : null}
          </div>
        ) : null}

        {!endDateOnly ? (
          <div className="form-field">
            <label htmlFor="ae-role">Role</label>
            <input
              id="ae-role"
              disabled={isSubmitting}
              {...register('role')}
            />
          </div>
        ) : null}

        {!endDateOnly ? (
          <>
            <div className="form-field">
              <label htmlFor="ae-bill">
                Bill rate (USD/hour)
                <span className="form-required" aria-hidden="true">*</span>
              </label>
              <input
                id="ae-bill"
                inputMode="decimal"
                disabled={isSubmitting}
                {...register('bill_rate', {
                  required: 'Bill rate is required.',
                  pattern: {
                    value: /^\d+(?:\.\d{1,2})?$/,
                    message: 'Enter dollars and cents.',
                  },
                })}
              />
              {errors.bill_rate ? (
                <span className="form-error">{errors.bill_rate.message}</span>
              ) : null}
            </div>
            <div className="form-field">
              <label htmlFor="ae-cost">
                Cost rate (USD/hour)
                <span className="form-required" aria-hidden="true">*</span>
              </label>
              <input
                id="ae-cost"
                inputMode="decimal"
                disabled={isSubmitting}
                {...register('cost_rate', {
                  required: 'Cost rate is required.',
                  pattern: {
                    value: /^\d+(?:\.\d{1,2})?$/,
                    message: 'Enter dollars and cents.',
                  },
                })}
              />
              {errors.cost_rate ? (
                <span className="form-error">{errors.cost_rate.message}</span>
              ) : null}
            </div>
            <div className="form-field">
              <label htmlFor="ae-alloc">
                Allocation %
                <span className="form-required" aria-hidden="true">*</span>
              </label>
              <input
                id="ae-alloc"
                type="number"
                min={0}
                max={100}
                step={0.1}
                disabled={isSubmitting}
                {...register('allocation_pct', {
                  required: 'Allocation is required.',
                  validate: (v) => {
                    const n = Number(v);
                    if (Number.isNaN(n)) return 'Enter a number.';
                    if (n < 0 || n > 100) return 'Must be between 0 and 100.';
                    return true;
                  },
                })}
              />
              {errors.allocation_pct ? (
                <span className="form-error">{errors.allocation_pct.message}</span>
              ) : null}
            </div>
            <div className="form-field">
              <label htmlFor="ae-start">
                Start date
                <span className="form-required" aria-hidden="true">*</span>
              </label>
              <input
                id="ae-start"
                type="date"
                disabled={isSubmitting}
                {...register('start_date', { required: 'Start date is required.' })}
              />
              {errors.start_date ? (
                <span className="form-error">{errors.start_date.message}</span>
              ) : null}
            </div>
          </>
        ) : null}

        <div className="form-field">
          <label htmlFor="ae-end">End date</label>
          <input
            id="ae-end"
            type="date"
            disabled={isSubmitting}
            {...register('end_date', {
              validate: (v) => {
                if (!v) return true;
                const start = watch('start_date');
                if (start && v < start)
                  return 'End date must be on or after the start date.';
                return true;
              },
            })}
          />
          {errors.end_date ? (
            <span className="form-error">{errors.end_date.message}</span>
          ) : null}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
            aria-busy={isSubmitting ? 'true' : 'false'}
          >
            {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create assignment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
